import { useEffect, useRef, useState, useCallback } from "react";
import { Play, Pause, SkipBack, SkipForward, Music2, ChevronDown, ChevronUp, Volume2, Shuffle } from "lucide-react";
import { api, API } from "@/lib/api";

const BACKEND = API.replace(/\/api$/, "");

const fmt = (s) => {
  if (!Number.isFinite(s) || s < 0) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

export default function SoundtrackPlayer() {
  const [tracks, setTracks] = useState([]);
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(0);
  const [dur, setDur] = useState(0);
  const [loading, setLoading] = useState(false);
  const audioRef = useRef(null);

  const load = useCallback(() => api.get("/soundtrack").then((r) => setTracks(r.data || [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const current = tracks[idx];
  const srcFor = (t) => `${BACKEND}${t.stream_url}`;

  // Point the element at the current track; also re-applies a freshly
  // refreshed stream token (the list is reloaded after a playback error).
  useEffect(() => {
    const a = audioRef.current;
    if (!a || !current) return;
    const want = srcFor(current);
    if (a.src === want) return;
    const wasPlaying = !a.paused;
    a.src = want;
    a.load();
    setPos(0);
    setDur(current.duration_sec || 0);
    if (wasPlaying || playing) {
      setLoading(true);
      a.play().catch(() => { setPlaying(false); setLoading(false); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.stream_url]);

  // iOS needs play() inside the tap itself, so switching tracks sets src and plays synchronously.
  const toggle = (i = idx) => {
    const a = audioRef.current;
    if (!a) return;
    if (i !== idx) {
      const t = tracks[i];
      setIdx(i);
      setOpen(true);
      setPos(0);
      setDur(t.duration_sec || 0);
      a.src = srcFor(t);
      a.load();
      setLoading(true);
      setPlaying(true);
      a.play().catch(() => { setPlaying(false); setLoading(false); });
      return;
    }
    if (a.paused) {
      if (!a.src) { a.src = srcFor(current); a.load(); }
      setLoading(true);
      a.play().then(() => setPlaying(true)).catch(() => { setLoading(false); load(); });
    } else {
      a.pause();
      setPlaying(false);
    }
  };
  const [shuffle, setShuffle] = useState(false);
  const historyRef = useRef([]); // indices played in shuffle mode, for "previous"

  const pickRandom = () => {
    if (tracks.length < 2) return idx;
    let r = idx;
    while (r === idx) r = Math.floor(Math.random() * tracks.length);
    return r;
  };
  const next = () => {
    if (!tracks.length) return;
    if (shuffle) {
      historyRef.current.push(idx);
      toggle(pickRandom());
    } else {
      toggle((idx + 1) % tracks.length);
    }
  };
  const prev = () => {
    if (!tracks.length) return;
    if (shuffle && historyRef.current.length) {
      toggle(historyRef.current.pop());
    } else {
      toggle((idx - 1 + tracks.length) % tracks.length);
    }
  };

  if (!tracks.length) return null;

  const total = dur || current?.duration_sec || 0;
  const pct = total ? Math.min(100, (pos / total) * 100) : 0;

  return (
    <section
      className="mx-4 my-3 rounded-2xl chalkboard-band border-4 border-brand-red shadow-lg text-white overflow-hidden"
      data-testid="soundtrack-player"
      onContextMenu={(e) => e.preventDefault()}
    >
      <audio
        ref={audioRef}
        preload="none"
        controlsList="nodownload noplaybackrate"
        onPlaying={() => { setPlaying(true); setLoading(false); }}
        onPause={() => setPlaying(false)}
        onWaiting={() => setLoading(true)}
        onTimeUpdate={(e) => setPos(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDur(e.currentTarget.duration)}
        onEnded={next}
        onError={() => { setLoading(false); setPlaying(false); load(); }}
      />
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="soundtrack-toggle"
        className="w-full flex items-center gap-3 px-4 py-3 text-left"
      >
        <span className="w-10 h-10 shrink-0 rounded-full bg-brand-yellow text-stone-900 flex items-center justify-center">
          <Music2 className="w-5 h-5" />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block font-display text-brand-yellow text-lg leading-tight">Our Soundtrack</span>
          <span className="block text-xs text-stone-300 truncate" data-testid="soundtrack-now-playing">
            {playing ? `Now playing · ${current.title}` : `${tracks.length} song${tracks.length === 1 ? "" : "s"} · tap to listen`}
          </span>
        </span>
        {open ? <ChevronUp className="w-5 h-5 text-stone-300" /> : <ChevronDown className="w-5 h-5 text-stone-300" />}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3" data-testid="soundtrack-body">
          <div className="rounded-xl bg-black/30 p-3">
            <div className="flex items-center gap-2 mb-2">
              <Volume2 className="w-4 h-4 text-brand-yellow shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-bold text-sm truncate" data-testid="soundtrack-current-title">{current.title}</div>
                {current.artist && <div className="text-xs text-stone-300 truncate">{current.artist}</div>}
              </div>
            </div>
            <input
              type="range"
              min={0}
              max={total || 0}
              step={0.1}
              value={Math.min(pos, total || 0)}
              onChange={(e) => {
                const v = Number(e.target.value);
                setPos(v);
                if (audioRef.current) audioRef.current.currentTime = v;
              }}
              aria-label="Seek"
              data-testid="soundtrack-seek"
              className="w-full accent-[rgb(var(--brand-yellow))] h-1.5"
              style={{ background: `linear-gradient(to right, rgb(var(--brand-yellow)) ${pct}%, rgba(255,255,255,0.2) ${pct}%)` }}
            />
            <div className="flex justify-between text-[11px] text-stone-300 mt-1">
              <span>{fmt(pos)}</span>
              <span>{fmt(total)}</span>
            </div>
            <div className="flex items-center justify-center gap-4 mt-2">
              <button type="button" onClick={prev} aria-label="Previous song" data-testid="soundtrack-prev"
                className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center active:scale-95 transition-transform">
                <SkipBack className="w-5 h-5" />
              </button>
              <button type="button" onClick={() => toggle()} aria-label={playing ? "Pause" : "Play"} data-testid="soundtrack-play"
                className="w-14 h-14 rounded-full bg-brand-yellow text-stone-900 flex items-center justify-center shadow-lg active:scale-95 transition-transform">
                {loading ? (
                  <span className="w-5 h-5 border-2 border-stone-900/30 border-t-stone-900 rounded-full animate-spin" />
                ) : playing ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-0.5" />}
              </button>
              <button type="button" onClick={next} aria-label="Next song" data-testid="soundtrack-next"
                className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center active:scale-95 transition-transform">
                <SkipForward className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={() => { setShuffle((s) => !s); historyRef.current = []; }}
                aria-label={shuffle ? "Shuffle on" : "Shuffle off"}
                aria-pressed={shuffle}
                data-testid="soundtrack-shuffle"
                title={shuffle ? "Shuffle on" : "Shuffle off"}
                className={`w-11 h-11 rounded-full flex items-center justify-center active:scale-95 transition-colors ${
                  shuffle ? "bg-brand-yellow text-stone-900 shadow-md" : "bg-white/10 hover:bg-white/20 text-white"
                }`}
              >
                <Shuffle className="w-5 h-5" />
              </button>
            </div>
            {shuffle && (
              <div className="text-center text-[11px] text-brand-yellow mt-2" data-testid="soundtrack-shuffle-hint">
                Shuffle on · songs play in random order
              </div>
            )}
          </div>

          <ol className="space-y-1 max-h-64 overflow-y-auto" data-testid="soundtrack-list">
            {tracks.map((t, i) => {
              const active = i === idx;
              return (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => toggle(i)}
                    data-testid={`soundtrack-track-${t.id}`}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left min-h-[44px] transition-colors ${
                      active ? "bg-brand-yellow/20 text-brand-yellow" : "hover:bg-white/10"
                    }`}
                  >
                    <span className="w-6 text-xs text-stone-400 tabular-nums shrink-0">
                      {active && playing ? <Pause className="w-3.5 h-3.5 inline" /> : i + 1}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-semibold truncate">{t.title}</span>
                      {t.artist && <span className="block text-[11px] text-stone-400 truncate">{t.artist}</span>}
                    </span>
                    <span className="text-[11px] text-stone-400 tabular-nums shrink-0">{fmt(t.duration_sec)}</span>
                  </button>
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </section>
  );
}
